import { useState, type ReactElement } from 'react';
import { v2, type Submission } from '@/api/v2Client';
import { describeApiError } from '@/lib/apiErrorView';
import {
  FDA_SUBMISSION_TYPES, FDA_SUBMISSION_SUBTYPES, FDA_SUPPLEMENT_EFFECTIVE_DATES,
  isValidFdaActivitySelection, fdaSupplementEffectiveDateOptions,
} from '@shared/fdaSubmissionVocabulary';
import { fdaActivityStartSequenceError } from '@shared/fdaActivityStartSequence';

interface Props {
  submission: Submission;
  canEdit: boolean;
  onSaved: (updated: Submission) => void;
}

/** Explicit review of ambiguous legacy labels; existing codes are never preselected. */
export function FdaActivityReview({ submission, canEdit, onSaved }: Props): ReactElement {
  const [activity, setActivity] = useState('');
  const [subtype, setSubtype] = useState('');
  const [effective, setEffective] = useState('');
  const [start, setStart] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const application = submission.SubmissionType ?? '';
  const subtypes = Object.entries(FDA_SUBMISSION_SUBTYPES)
    .filter(([code]) => isValidFdaActivitySelection(application, activity, code));
  const effectiveOptions = fdaSupplementEffectiveDateOptions(activity, subtype);
  const valid = isValidFdaActivitySelection(application, activity, subtype)
    && (effectiveOptions.length === 0 || effectiveOptions.includes(effective))
    && !fdaActivityStartSequenceError(start, submission.SequenceNumber, { required: true });

  async function save(): Promise<void> {
    if (!canEdit || !valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await v2.submissions.update(submission.Id, {
        SubmissionTypeCode: activity, SubmissionSubTypeCode: subtype,
        SupplementEffectiveDateType: effective || null, FdaActivityStartSequence: start,
      });
      onSaved(updated);
    } catch (err) {
      setError(describeApiError(err, 'The FDA metadata review could not be saved.').message);
    } finally { setSaving(false); }
  }

  return <section aria-label="Review FDA activity" style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, marginTop: 12 }}>
    <h3 style={{ marginTop: 0 }}>Review FDA activity</h3>
    <p>{submission.FdaActivityMetadataNeedsReview ? 'Earlier activity labels were incorrect. ' : 'Complete the activity metadata before compilation. '}Choose the activity and subtype that describe this filing. Saved codes remain unchanged until you confirm.</p>
    {!canEdit && <p>You need edit access to an unlocked sequence to confirm this review.</p>}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
      <label>Activity <select aria-label="Reviewed FDA activity" value={activity} disabled={!canEdit || saving}
        onChange={event => { setActivity(event.target.value); setSubtype(''); setEffective(''); }}>
        <option value="">Choose activity…</option>
        {Object.entries(FDA_SUBMISSION_TYPES).filter(([code]) => Object.keys(FDA_SUBMISSION_SUBTYPES)
          .some(sub => isValidFdaActivitySelection(application, code, sub)))
          .map(([code, label]) => <option key={code} value={code}>{label}</option>)}
      </select></label>
      <label>Subtype <select aria-label="Reviewed FDA subtype" value={subtype} disabled={!canEdit || saving || !activity}
        onChange={event => { setSubtype(event.target.value); setEffective(''); }}>
        <option value="">Choose subtype…</option>
        {subtypes.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
      </select></label>
      {effectiveOptions.length > 0 && <label>Supplement category <select aria-label="Reviewed FDA supplement category"
        value={effective} disabled={!canEdit || saving} onChange={event => setEffective(event.target.value)}>
        <option value="">Choose category…</option>
        {effectiveOptions.map(code => <option key={code} value={code}>{FDA_SUPPLEMENT_EFFECTIVE_DATES[code as keyof typeof FDA_SUPPLEMENT_EFFECTIVE_DATES]}</option>)}
      </select></label>}
      <label>Activity starting sequence <input aria-label="Reviewed FDA activity starting sequence" value={start}
        maxLength={4} inputMode="numeric" placeholder="0000" disabled={!canEdit || saving}
        onChange={event => setStart(event.target.value)} /></label>
    </div>
    <p style={{ fontSize: 12 }}>Enter the four-digit sequence where this FDA activity began. It cannot be later than this sequence ({submission.SequenceNumber}).</p>
    {error && <p role="alert">{error}</p>}
    <button type="button" disabled={!canEdit || !valid || saving} onClick={() => { void save(); }}>
      {saving ? 'Saving review…' : 'Confirm FDA metadata review'}
    </button>
  </section>;
}
